/**
 * `<ShareDialog>` — create or manage a private deck's share subdomain
 * (issue #8). Presentational: the parent owns `isOpen` and the `useShares`
 * actions; this component owns the form fields + submit/error UI.
 *
 *   - No `existingShare` → CREATE mode: subdomain label + allowlist, then
 *     `onCreate`.
 *   - `existingShare` set → MANAGE mode: edit the allowlist (`onUpdate`) or
 *     stop sharing (`onDelete`). The host is fixed once created.
 *
 * Server errors (400 invalid, 409 conflict, 503 provisioning-not-configured)
 * surface inline via the thrown Error's message. A create-mode note reminds
 * the author that the share only serves once its bundle is built + deployed
 * (the Worker cannot build at runtime — see issue #8 / docs/deploy.md).
 *
 * Visual language matches `<ConfirmDialog>` (warm cream panel, brown border,
 * orange accent; no pure red in the token set).
 */

import { AnimatePresence, motion, type HTMLMotionProps } from "framer-motion";
import { useEffect, useState } from "react";
import { easeStandard } from "@/lib/motion";
import type { CreateShareInput, ShareRecord, UpdateShareInput } from "@/lib/shares";

export interface ShareDialogProps {
  isOpen: boolean;
  /** The private deck being shared. */
  deckSlug: string;
  /** Human title for display; falls back to the slug. */
  deckTitle?: string;
  /** Present → MANAGE an existing share; absent/null → CREATE a new one. */
  existingShare?: ShareRecord | null;
  /** Share apex for the subdomain suffix label. */
  apex?: string;
  onClose: () => void;
  onCreate: (input: CreateShareInput) => Promise<void>;
  onUpdate: (host: string, allowlist: UpdateShareInput) => Promise<void>;
  onDelete: (host: string) => Promise<void>;
}

const DEFAULT_APEX = "lusostreams.com";

/** Split a comma / whitespace / newline list into trimmed, lowercased, non-empty entries. */
function parseList(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

const backdropMotion: HTMLMotionProps<"div"> = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15, ease: easeStandard },
};

const panelMotion: HTMLMotionProps<"div"> = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.18, ease: easeStandard },
};

const inputClass =
  "mt-1 block w-full rounded border border-cf-border bg-cf-bg-100 px-2 py-1 font-mono text-xs text-cf-text focus:border-cf-orange focus:outline-none";

export function ShareDialog({
  isOpen,
  deckSlug,
  deckTitle,
  existingShare,
  apex = DEFAULT_APEX,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: ShareDialogProps) {
  const manage = !!existingShare;
  const [subdomain, setSubdomain] = useState("");
  const [emails, setEmails] = useState("");
  const [domains, setDomains] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed fields on open (and whenever the managed share changes).
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSubmitting(false);
    if (existingShare) {
      setEmails(existingShare.allowedEmails.join(", "));
      setDomains(existingShare.allowedDomains.join(", "));
    } else {
      setSubdomain("");
      setEmails("");
      setDomains("");
    }
  }, [isOpen, existingShare]);

  // Esc closes (but not mid-submit, to avoid abandoning a provisioning call).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const allowedEmails = parseList(emails);
  const allowedDomains = parseList(domains).map((d) => d.replace(/^@/, ""));
  const hasAllowlist = allowedEmails.length + allowedDomains.length > 0;

  async function handleSubmit() {
    setError(null);
    if (!hasAllowlist) {
      setError(
        "Add at least one allowed email or domain. An empty allowlist would lock everyone out.",
      );
      return;
    }
    if (!manage && !subdomain.trim()) {
      setError("Enter a subdomain label.");
      return;
    }
    setSubmitting(true);
    try {
      if (manage && existingShare) {
        await onUpdate(existingShare.host, { allowedEmails, allowedDomains });
      } else {
        await onCreate({
          slug: deckSlug,
          subdomain: subdomain.trim().toLowerCase(),
          allowedEmails,
          allowedDomains,
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!existingShare) return;
    setError(null);
    setSubmitting(true);
    try {
      await onDelete(existingShare.host);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          {...backdropMotion}
          data-testid="share-dialog-backdrop"
          className="fixed inset-0 z-50 flex items-center justify-center bg-cf-text/30 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget && !submitting) onClose();
          }}
        >
          <motion.div
            {...panelMotion}
            role="dialog"
            aria-modal="true"
            data-testid="share-dialog"
            data-no-advance
            className="relative w-full max-w-md rounded-lg border border-cf-border bg-cf-bg-100 p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-1 text-lg font-medium tracking-[-0.02em] text-cf-text">
              {manage ? "Manage share" : "Share this deck"}
            </h2>
            <p className="mb-4 text-sm text-cf-text-muted">
              {manage ? (
                <>
                  Serving{" "}
                  <code className="rounded bg-cf-bg-200 px-1 py-0.5 font-mono text-[12px] text-cf-text">
                    {existingShare?.host}
                  </code>{" "}
                  for <strong>{deckTitle ?? deckSlug}</strong>.
                </>
              ) : (
                <>
                  Give <strong>{deckTitle ?? deckSlug}</strong> a private
                  subdomain that only the people you list can open (one-time PIN
                  over Cloudflare Access).
                </>
              )}
            </p>

            <div className="flex flex-col gap-3">
              {!manage && (
                <label className="block text-xs font-medium text-cf-text">
                  <span>Subdomain</span>
                  <span className="mt-1 flex items-stretch overflow-hidden rounded border border-cf-border bg-cf-bg-100 focus-within:border-cf-orange">
                    <input
                      type="text"
                      data-testid="share-dialog-subdomain"
                      data-interactive
                      value={subdomain}
                      onChange={(e) => {
                        setSubdomain(e.target.value);
                        setError(null);
                      }}
                      placeholder="acme"
                      autoFocus
                      className="block w-full bg-transparent px-2 py-1 font-mono text-xs text-cf-text focus:outline-none"
                    />
                    <span className="flex items-center border-l border-cf-border bg-cf-bg-200 px-2 font-mono text-xs text-cf-text-muted">
                      .{apex}
                    </span>
                  </span>
                </label>
              )}

              <label className="block text-xs font-medium text-cf-text">
                <span>Allowed emails</span>
                <input
                  type="text"
                  data-testid="share-dialog-emails"
                  data-interactive
                  value={emails}
                  onChange={(e) => {
                    setEmails(e.target.value);
                    setError(null);
                  }}
                  placeholder="alex@bbc.co.uk, sam@bbc.co.uk"
                  className={inputClass}
                />
              </label>

              <label className="block text-xs font-medium text-cf-text">
                <span>
                  Allowed email domains{" "}
                  <span className="text-cf-text-subtle">(optional)</span>
                </span>
                <input
                  type="text"
                  data-testid="share-dialog-domains"
                  data-interactive
                  value={domains}
                  onChange={(e) => {
                    setDomains(e.target.value);
                    setError(null);
                  }}
                  placeholder="bbc.co.uk"
                  className={inputClass}
                />
              </label>

              <p className="text-xs text-cf-text-subtle">
                Comma-separated. At least one email or domain is required.
              </p>
            </div>

            {!manage && (
              <p
                data-testid="share-dialog-build-note"
                className="mt-3 rounded border border-cf-border bg-cf-bg-50 px-3 py-2 text-xs text-cf-text-muted"
              >
                After creating, ship the share bundle so the subdomain serves
                the deck:{" "}
                <code className="font-mono text-[11px] text-cf-text">
                  npm run deploy:share -- {deckSlug}
                </code>
                .
              </p>
            )}

            {error && (
              <p
                role="alert"
                data-testid="share-dialog-error"
                className="mt-3 rounded border border-cf-orange/40 bg-cf-orange-light px-3 py-2 text-xs text-cf-orange"
              >
                {error}
              </p>
            )}

            <div className="mt-5 flex items-center justify-between gap-2">
              <div>
                {manage && (
                  <button
                    type="button"
                    data-interactive
                    data-testid="share-dialog-delete"
                    disabled={submitting}
                    onClick={() => void handleDelete()}
                    className="inline-flex items-center justify-center rounded border border-cf-orange bg-cf-orange px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cf-bg-100 transition-colors hover:border-dashed disabled:opacity-50"
                  >
                    Stop sharing
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-interactive
                  data-testid="share-dialog-cancel"
                  onClick={onClose}
                  disabled={submitting}
                  className="cf-btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-interactive
                  data-testid="share-dialog-submit"
                  onClick={() => void handleSubmit()}
                  disabled={submitting}
                  className="cf-btn-primary disabled:opacity-50"
                >
                  {submitting
                    ? manage
                      ? "Saving"
                      : "Creating"
                    : manage
                      ? "Save allowlist"
                      : "Create share"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
